import React, { Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, useNavigate, createMemoryRouter } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { redirects } from './routes';
import LoadingSpinner from '../components/LoadingSpinner';
import PageTransition from '../components/PageTransition';
import LazyErrorBoundary from '../utils/LazyErrorBoundary';
import URLErrorDetector from '../components/URLErrorDetector';

// Direkte Imports statt Lazy Loading für besseres Debugging
import HomePage from '../pages/HomePage';
import AGBPage from '../pages/AGBPage';
import ImpressumPage from '../pages/ImpressumPage';
import PrivacyPage from '../pages/PrivacyPage';
import AffiliatePage from '../pages/AffiliatePage';
import BeratungsterminPage from '../pages/BeratungsterminPage';
import ServiceDetailPage from '../pages/ServiceDetailPage';
import NotFoundPage from '../pages/NotFoundPage';
import KundenWartungPage from '../pages/KundenWartungPage';

// Root Layout Komponente
const RootLayout: React.FC = () => {
  const location = useLocation();

  return (
    <LazyErrorBoundary>
      <URLErrorDetector>
        <AnimatePresence mode="wait" onExitComplete={() => window.scrollTo(0, 0)}>
          <PageTransition key={location.pathname}>
            <Suspense fallback={<LoadingSpinner size="large" text="Seite wird geladen..." />}>
              <RedirectHandler />
              <Outlet />
            </Suspense>
          </PageTransition>
        </AnimatePresence>
      </URLErrorDetector>
    </LazyErrorBoundary>
  );
};

// Redirect Handler für Legacy URLs
const RedirectHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;

    // Exakter Match
    let redirectTarget = redirects[currentPath];

    // /de/* -> / (Sprachprafix-Redirect)
    if (!redirectTarget && (currentPath === '/de' || currentPath.startsWith('/de/'))) {
      redirectTarget = '/';
    }

    if (redirectTarget) {
      navigate(redirectTarget, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

// Router-Konfiguration exportieren für SSR
export const routes = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: [
      // Direkte Route-Definitionen
      {
        index: true,
        element: <HomePage />
      },
      {
        path: 'agb',
        element: <AGBPage />
      },
      {
        path: 'impressum',
        element: <ImpressumPage />
      },
      {
        path: 'datenschutz',
        element: <PrivacyPage />
      },
      {
        path: 'affiliate',
        element: <AffiliatePage />
      },
      {
        path: 'beratungstermin',
        element: <BeratungsterminPage />
      },
      {
        path: 'wartung',
        element: <KundenWartungPage />
      },
      // Sprach-Prefix /de und /de/* -> Redirect zu /
      {
        path: 'de',
        element: <HomePage />
      },
      {
        path: 'de/*',
        element: <HomePage />
      },
      {
        path: ':serviceId',
        element: <ServiceDetailPage />
      },
      // 404 Fallback
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
];

// Router Erstellung (Browser-safe)
const router = import.meta.env.SSR 
  ? createMemoryRouter(routes)
  : createBrowserRouter(routes);

// Router Provider Komponente
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
